import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { environment } from 'src/environments/environment';
import Swal from 'sweetalert2';
import { ProfileDataService } from '../service/profile-data.service';
import { HeaderService } from '../../service/header.service';

@Component({
  selector: 'app-alterar-senha',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterModule],
  templateUrl: './alterar-senha.component.html',
  styleUrls: ['./alterar-senha.component.scss'],
})
export class AlterarSenhaComponent implements OnInit {
  BASE_IMG_URL: string = environment.BASE_IMG_URL;

  senhaForm!: FormGroup;
  showCurrentPassword = false;
  showNewPassword = false;
  showConfirmPassword = false;
  submitting = false;
  password: string = '';
  passwordsDoNotMatch = false;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private headerService: HeaderService,
    private profileDataService: ProfileDataService,
  ) {}

  ngOnInit() {
    this.initializeForm();
    this.loadProfileData();
  }

  loadProfileData(): void {
    this.profileDataService.loadProfileData().subscribe(
      (response: any) => {
        this.password = this.profileDataService.getPassword();
        if (this.password) {
          this.senhaForm.patchValue({
            senhaAtual: this.password,
          });
        }
      },
      (error) => {
        console.error('Erro ao carregar dados do perfil:', error);
      },
    );
  }

  initializeForm(): void {
    this.senhaForm = this.fb.group({
      senhaAtual: ['', [Validators.required]],
      novaSenha: [
        '',
        [
          Validators.required,
          Validators.minLength(8),
          this.strongPasswordValidator,
        ],
      ],
      confirmarSenha: ['', [Validators.required]],
    });

    this.senhaForm.get('novaSenha')?.valueChanges.subscribe(() => {
      this.checkPasswordsMatch();
    });

    this.senhaForm.get('confirmarSenha')?.valueChanges.subscribe(() => {
      this.checkPasswordsMatch();
    });
  }

  strongPasswordValidator(control: any) {
    const senha = control.value;

    if (!senha) return null;

    const hasUpperCase = /[A-Z]/.test(senha);
    const hasLowerCase = /[a-z]/.test(senha);
    const hasNumber = /[0-9]/.test(senha);
    const hasSpecialChar = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(senha);

    const isValid = hasUpperCase && hasLowerCase && hasNumber && hasSpecialChar;

    return isValid ? null : { senhaFraca: true };
  }

  checkPasswordsMatch(): void {
    const newPassword = this.senhaForm.get('novaSenha')?.value;
    const confirmPassword = this.senhaForm.get('confirmarSenha')?.value;

    this.passwordsDoNotMatch = confirmPassword && newPassword !== confirmPassword;
  }

  get isFormValid(): boolean {
    return this.senhaForm.valid && !this.passwordsDoNotMatch && !this.submitting;
  }

  toggleShowPassword(campo: 'atual' | 'nova' | 'confirmar'): void {
    switch (campo) {
      case 'atual':
        this.showCurrentPassword = !this.showCurrentPassword;
        break;
      case 'nova':
        this.showNewPassword = !this.showNewPassword;
        break;
      case 'confirmar':
        this.showConfirmPassword = !this.showConfirmPassword;
        break;
    }
  }

  changePassword(): void {
    if (!this.senhaForm.valid) return;

    this.submitting = true;

    const currentPassword = this.senhaForm.get('senhaAtual')?.value;
    const newPassword = this.senhaForm.get('novaSenha')?.value;
    const confirmNewPassword = this.senhaForm.get('confirmarSenha')?.value;

    const data = {
      currentPassword,
      newPassword,
      confirmNewPassword,
    };

    this.headerService.updatePassword(data).subscribe(
      (response: any) => {
        this.submitting = false;
        if (response && response.success) {
          Swal.fire({
            text: 'Senha alterada com sucesso!',
            icon: 'success',
            confirmButtonText: 'OK',
          });

          this.senhaForm.reset();
          this.passwordsDoNotMatch = false;
          this.loadProfileData();
        } else {
          Swal.fire({
            text:
              response?.message || 'Erro ao alterar a senha. Tente novamente.',
            icon: 'error',
            confirmButtonText: 'OK',
          });
        }
      },
      (error) => {
        this.submitting = false;
        console.error('Erro ao alterar senha:', error);
        const mensagem =
          error?.error?.message ||
          'Erro ao alterar a senha. Verifique sua conexão e tente novamente.';
        Swal.fire({
          text: mensagem,
          icon: 'error',
          confirmButtonText: 'OK',
        });
      },
    );
  }

  goBackToProfile(): void {
    this.router.navigate(['/usuario']);
  }
}
